// Shir-Derech questionnaires (opening + closing), shared by the public
// forms (/form/shir-derech/*) and the response screens (/shir-derech/*).
// Question texts copied verbatim from the תשפ"ו questionnaires.

export const SHIR_DERECH_YEAR = 'תשפ"ז';

export type QuestionnaireKind = "opening" | "closing";

export const KIND_LABEL: Record<QuestionnaireKind, string> = {
  opening: "פתיחה",
  closing: "סיום",
};

type ScaleKey = "frequency" | "agreement" | "satisfaction" | "emotional";

export const SCALES: Record<ScaleKey, string[]> = {
  frequency: ["כמעט אף פעם", "לעיתים רחוקות", "לפעמים", "לעיתים קרובות", "כמעט תמיד"],
  agreement: ["לא נכון בכלל", "נכון במידה מועטה", "נכון במידה בינונית", "נכון במידה רבה", "נכון כמעט תמיד"],
  satisfaction: ["לא מסכים בכלל", "לא ממש מסכים", "מסכים במידה בינונית", "מסכים", "מסכים מאוד"],
  emotional: ["כלל לא", "במידה מועטה", "במידה בינונית", "במידה רבה", "במידה רבה מאוד"],
};

export type ScaleQuestion = { id: number; text: string };

export type Domain = {
  id: string;
  title: string;
  subtitle: string;
  scale: ScaleKey;
  questions: ScaleQuestion[];
};

export const DOMAINS: Domain[] = [
  {
    id: "who5",
    title: "תחום 1: רווחה נפשית (WHO-5)",
    subtitle: "בשבועיים האחרונים: באיזו תדירות?",
    scale: "frequency",
    questions: [
      { id: 1, text: "הייתי במצב רוח טוב ושמח" },
      { id: 2, text: "הרגשתי רגוע ונינוח" },
      { id: 3, text: "הרגשתי פעיל ומלא אנרגיה" },
      { id: 4, text: "התעוררתי בבוקר בתחושה טובה ורעננה" },
      { id: 5, text: "הרגשתי שהיום שלי היה מלא בעניין" },
    ],
  },
  {
    id: "cdrisc",
    title: "תחום 2: חוסן נפשי (CD-RISC-10)",
    subtitle: "עד כמה כל אחד מהמשפטים הבאים נכון לגביך?",
    scale: "agreement",
    questions: [
      { id: 6, text: "אני מסתגל במהירות לשינויים" },
      { id: 7, text: "אני מתאושש מהר אחרי אתגרים או נפילות" },
      { id: 8, text: "אני רואה בקשיים אתגר ולא איום" },
      { id: 9, text: "אני מסוגל לשמור על רוגע גם כשיש לחץ" },
      { id: 10, text: "גם כשדברים נראים קשים, אני מאמין שאני יכול להתמודד איתם" },
    ],
  },
  {
    id: "rses",
    title: "תחום 3: דימוי עצמי (RSES)",
    subtitle: "עד כמה אתה מסכים עם כל אחד מהמשפטים הבאים?",
    scale: "satisfaction",
    questions: [
      { id: 11, text: "באופן כללי, אני מרוצה מעצמי" },
      { id: 12, text: "אני מרגיש שיש לי תכונות טובות" },
      { id: 13, text: "אני מרגיש שאני אדם בעל ערך, לפחות ברמה שווה לאחרים" },
      { id: 14, text: "אני מרגיש תחושת סיפוק מהדברים שאני עושה" },
      { id: 15, text: "אני מרגיש שיש לי שליטה על חיי" },
    ],
  },
  {
    id: "additional",
    title: "תחום 4: תקווה, משמעות ואמונה עצמית",
    subtitle: "עד כמה אתה מסכים עם כל אחד מהמשפטים הבאים?",
    scale: "satisfaction",
    questions: [
      { id: 16, text: "אני מאמין שהעתיד שלי יהיה טוב" },
      { id: 17, text: "אני מאמין בעצמי וביכולותי ליצור לעצמי חיים טובים" },
      { id: 18, text: "אני מאמין שיש לי את היכולת להשיג את המטרות שלי" },
      { id: 19, text: "חיי מלאים במשמעות" },
      { id: 20, text: "אני מרגיש שיש לי סיבות טובות לקום בבוקר" },
    ],
  },
  {
    id: "panas",
    title: "תחום 5: מצב רגשי ונפשי (PANAS)",
    subtitle: "באיזו מידה חווית את הרגשות הבאים בשבועיים האחרונים?",
    scale: "emotional",
    questions: [
      { id: 21, text: "הרגשתי מלא תקווה או אופטימי" },
      { id: 22, text: "הרגשתי מעורב ומתעניין בדברים סביבי" },
      { id: 23, text: "הרגשתי עצוב או מדוכא" },
      { id: 24, text: "הרגשתי מפוחד או חרד" },
      { id: 25, text: "הרגשתי מתוסכל או עצבני" },
    ],
  },
];

export const SCALE_QUESTION_IDS = DOMAINS.flatMap((d) => d.questions.map((q) => q.id));

export const OPEN_QUESTIONS: Record<QuestionnaireKind, ScaleQuestion[]> = {
  opening: [
    { id: 26, text: 'מה מביא אותך לתכנית "שיר דרך"? מהן הציפיות והתקווות שלך?' },
    { id: 27, text: "מהי המשמעות של מוזיקה עבורך?" },
    { id: 28, text: "האם יש משהו ספציפי שתרצה שנדע עליך לפני תחילת התכנית?" },
  ],
  closing: [
    { id: 26, text: 'כיצד התכנית "שיר דרך" השפיעה עליך? אנא תאר בצורה כללית את ההשפעה (רגשית, נפשית, חברתית).' },
    { id: 27, text: "האם המוזיקה והשיעורים עזרו לך באופן ספציפי בהתמודדות עם קשיים ורגשות מסוימים? אנא פרט." },
    { id: 28, text: "מה היו הרגעים המשמעותיים ביותר עבורך בתכנית? מדוע הם היו משמעותיים?" },
    { id: 29, text: "האם היית ממליץ על התכנית לאחרים? אם כן, למה?" },
    { id: 30, text: "מה היית משפר או משנה בתכנית?" },
  ],
};

export const OPEN_MAX_LENGTH = 1500;

export type ShirDerechResponse = {
  id: number;
  year: string;
  kind: QuestionnaireKind;
  full_name: string;
  scores: Record<string, number>;
  answers: Record<string, string>;
  created_at: string;
};

export function average(values: number[]): number | null {
  const nums = values.filter((v) => typeof v === "number" && !Number.isNaN(v));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export function domainAverage(r: ShirDerechResponse, d: Domain): number | null {
  return average(d.questions.map((q) => Number(r.scores[String(q.id)])));
}

export function overallAverage(r: ShirDerechResponse): number | null {
  return average(SCALE_QUESTION_IDS.map((id) => Number(r.scores[String(id)])));
}

export function fmtAvg(n: number | null): string {
  return n === null ? "—" : n.toFixed(1);
}

const dateTimeFmt = new Intl.DateTimeFormat("he-IL", {
  timeZone: "Asia/Jerusalem",
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export function fmtDateTime(iso: string): string {
  return dateTimeFmt.format(new Date(iso));
}

// "2026-11-03 14:05" in Israel time, for CSV.
export function isoLocal(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
}

function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: ShirDerechResponse[]): string {
  const header = [
    "משתתף",
    "סוג שאלון",
    "תאריך",
    ...SCALE_QUESTION_IDS.map((id) => `ש${id}`),
    "ש26",
    "ש27",
    "ש28",
    "ש29",
    "ש30",
  ];
  const lines = [header.map(csvCell).join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.full_name,
        KIND_LABEL[r.kind],
        isoLocal(r.created_at),
        ...SCALE_QUESTION_IDS.map((id) => r.scores[String(id)] ?? ""),
        ...[26, 27, 28, 29, 30].map((id) => r.answers[String(id)] ?? ""),
      ]
        .map(csvCell)
        .join(","),
    );
  }
  return lines.join("\r\n");
}

export function downloadCsv(rows: ShirDerechResponse[], filename: string) {
  // BOM so Excel opens Hebrew correctly.
  const blob = new Blob(["﻿" + toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function publicFormPath(kind: QuestionnaireKind): string {
  return `/form/shir-derech/${kind}`;
}
